import { track } from "./data-layer";
import { resolvePage } from "./page-view";

/**
 * The onboarding funnel's events: `onboarding_step_completed`, and `onboarding_abandoned` when the
 * person leaves before the funnel ends.
 *
 * **The funnel** is the host's way from the CPF to a saved curation: the identification
 * (`/onboarding`, `/onboarding/presentear`) and the list creation (`/anfitriao/nova-lista/**`) —
 * the routes whose `page_type` is `onboarding`.
 *
 * **The internal list creation** (`internal_new_list`, `/admin/nova-lista/**`) reports its completed
 * steps too: INST §09 lists `onboarding_step_completed` for that surface, with that `page_type`. It
 * shares the creation's components with the host's, so the steps it can report are the ones those
 * components send. It has no abandonment: INST §09 gives `onboarding_abandoned` to the `onboarding`
 * surface only. Everywhere else these functions do nothing, which keeps the public pages that share
 * some of the same components out of the funnel.
 *
 * Steps are named by what the person did, in English and in a closed set. Never a value they typed.
 */

export type OnboardingStep =
  | "cpf"
  | "login_code"
  | "signup"
  | "address"
  | "register_code"
  | "signup_login_code"
  | "event"
  | "terms"
  | "starting_point"
  | "suggestion_choice"
  | "suggestion_preview"
  | "curation";

/** What the funnel knows about this document. A new document starts again: nothing is kept in the browser. */
const state = { lastStep: null as OnboardingStep | null, isFinished: false, isLeaving: false, hasAbandoned: false };

function isOnboardingRoute(): boolean {
  return typeof window !== "undefined" && resolvePage(window.location.pathname).pageType === "onboarding";
}

/** The two surfaces INST §09 gives `onboarding_step_completed`: the host's creation and the internal one. */
function isStepSurface(): boolean {
  if (typeof window === "undefined") return false;

  const { pageType } = resolvePage(window.location.pathname);

  return pageType === "onboarding" || pageType === "internal_new_list";
}

/** The step a route stands for, for a document that has not completed any yet. */
export function stepForPathname(pathname: string): OnboardingStep | null {
  if (pathname === "/onboarding" || pathname === "/onboarding/presentear") return "cpf";

  const page = resolvePage(pathname);
  if (page.pageType !== "onboarding") return null;

  if (pathname === "/anfitriao/nova-lista") return "event";
  if (pathname === "/anfitriao/nova-lista/termos") return "terms";
  if (pathname === "/anfitriao/nova-lista/ponto-de-partida") return "starting_point";
  if (pathname === "/anfitriao/nova-lista/listas-de-sugestao") return "suggestion_choice";
  if (pathname.startsWith("/anfitriao/nova-lista/listas-de-sugestao/")) return "suggestion_preview";
  if (pathname === "/anfitriao/nova-lista/curadoria") return "curation";

  return null;
}

/** A step the person has just finished. A no-op outside the host's and the internal creation. */
export function completeOnboardingStep(step: OnboardingStep): void {
  if (!isStepSurface()) return;

  state.lastStep = step;
  track({ event: "onboarding_step_completed", step });
}

/** The funnel reached its end (the curation was saved): leaving afterwards is not abandoning. */
export function finishOnboarding(): void {
  state.isFinished = true;
}

/**
 * The page is about to be replaced by another **inside** the flow (`leaveForDocument`): the document
 * ends, but the person is not leaving. Without this every step that is a full page load would be
 * reported as an abandonment.
 */
export function noteOnboardingNavigation(): void {
  state.isLeaving = true;
}

/**
 * Reports the abandonment, once, when the page is hidden for good (`pagehide`) with the funnel
 * unfinished. The last step is the last one completed in this document, or the step the route stands for.
 */
export function reportAbandonment(): void {
  if (state.hasAbandoned || state.isFinished || state.isLeaving || !isOnboardingRoute()) return;

  const lastStep = state.lastStep ?? stepForPathname(window.location.pathname);
  if (lastStep === null) return;

  state.hasAbandoned = true;
  track({ event: "onboarding_abandoned", last_step: lastStep });
}

/** A page restored from the back/forward cache is a new chance to leave. */
export function resetOnboardingNavigation(): void {
  state.isLeaving = false;
}

/** For tests. */
export function resetOnboardingFunnel(): void {
  state.lastStep = null;
  state.isFinished = false;
  state.isLeaving = false;
  state.hasAbandoned = false;
}
